"use client";

import { cn } from "../../../../../../lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import ArrowDown from "../../../../../../icons/normal/chevron-down";

const selectedBadgeVariants = cva(
  "absolute top-1/2 p-[2px] -translate-y-1/2 flex items-center gap-[2px] rounded-xl z-10",
  {
    variants: {
      variant: {
        filled:
          "bg-semantic-secondary-background-heavy-defualt border border-semantic-secondary-stroke-strong-default",
        outlined:
          "bg-semantic-secondary-background-normal border border-semantic-secondary-stroke-normal",
        transparent: "bg-semantic-secondary-background-normal",
      },
    },
    defaultVariants: {
      variant: "filled",
    },
  }
);

const iconOverlayStyles = cva("absolute inset-0 opacity-0 cursor-pointer");
export interface SelectDropdownFieldProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof selectDropdownFieldVariants> {
  placeholder?: string;
  showPlaceholder?: boolean;
  selectedCount?: number;
  onClear?: () => void;
  value: string | string[];
  error?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  plainReadOnly?: boolean;
  open?: boolean;
  mode?: "single" | "multiple";
  icon?: React.ReactNode;
  clearIcon?: React.ReactNode;
  size?: "xsm" | "sm" | "md" | "lg" | "xlg";
}
const selectDropdownFieldVariants = cva(
  "relative flex-1 rounded px-sp160 py-sp80 transition-colors text-semantic-secondary-foreground-normal-default ring-0 outline-none focus:ring-0 focus:outline-none focus-visible:outline-none",
  {
    variants: {
      variant: {
        filled:
          "bg-semantic-secondary-background-strong-default hover:bg-semantic-secondary-background-strong-hovered active:bg-semantic-secondary-background-strong-pressed disabled:bg-semantic-secondary-background-strong-default disabled:hover:bg-semantic-secondary-background-strong-default",
        outlined:
          "bg-transparent border border-semantic-secondary-stroke-normal-default hover:border-semantic-secondary-stroke-strong disabled:border-semantic-secondary-stroke-normal-default disabled:hover:border-semantic-secondary-stroke-normal-default",
        transparent:
          "bg-transparent border-0 hover:bg-semantic-secondary-background-heavy/50 disabled:hover:bg-transparent",
      },
      size: {
        xsm: "h-6",
        sm: "h-7",
        md: "h-8",
        lg: "h-9",
        xlg: "h-10",
      },
    },
    defaultVariants: {
      variant: "filled",
      size: "md",
    },
  }
);

// 읽기 전용 상태를 위한 별도의 스타일 변형 정의
const readonlyVariants = cva("", {
  variants: {
    variant: {
      filled:
        "!bg-semantic-secondary-background-strong-default !hover:bg-semantic-secondary-background-strong-default",
      outlined:
        "!border-semantic-secondary-stroke-normal-default !hover:border-semantic-secondary-stroke-normal-default",
      transparent: "!hover:bg-transparent",
    },
  },
  defaultVariants: {
    variant: "filled",
  },
});

const SelectDropdownField = React.forwardRef<
  HTMLDivElement,
  SelectDropdownFieldProps
>(
  (
    {
      className,
      variant,
      placeholder = "Select options",
      showPlaceholder = true,
      selectedCount,
      onClear,
      value,
      error,
      disabled,
      readonly = false,
      plainReadOnly = false,
      open,
      mode = "single",
      icon,
      clearIcon,
      size,
      ...props
    },
    ref
  ) => {
    const renderSelectedValues = () => {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return (
            <span
              className={cn(
                "text-semantic-secondary-foreground-inverse-default",
                (disabled || readonly || plainReadOnly) && "opacity-50"
              )}
            >
              {placeholder}
            </span>
          );
        }
        return showPlaceholder
          ? `${placeholder}: ${value.join(", ")}`
          : value.join(", ");
      }
      if (!value) {
        return (
          <span
            className={cn(
              "text-semantic-secondary-foreground-inverse-default",
              (disabled || readonly || plainReadOnly) && "opacity-50"
            )}
          >
            {placeholder}
          </span>
        );
      }
      return showPlaceholder ? `${placeholder}: ${value}` : value;
    };
    const handleClearClick = (e: React.MouseEvent) => {
      if (disabled || readonly || plainReadOnly) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onClear?.();
    };

    const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
    const isInteractive = !(disabled || readonly || plainReadOnly);

    return (
      <div
        ref={ref}
        className={cn(
          selectDropdownFieldVariants({ variant, className }),
          "flex items-center gap-sp80",
          isInteractive ? "cursor-pointer" : "!cursor-default",
          disabled && "opacity-50",
          readonly &&
            !plainReadOnly &&
            "bg-semantic-secondary-background-strong-default",
          (readonly || plainReadOnly) && readonlyVariants({ variant }),
          (readonly || plainReadOnly) && "!pointer-events-none !cursor-default"
        )}
        aria-disabled={disabled || readonly || plainReadOnly}
        {...props}
      >
        {hasValue && (
          <>
            {mode === "multiple" ? (
              <div
                className={cn(
                  "pl-0",
                  selectedBadgeVariants({ variant }),
                  (disabled || readonly || plainReadOnly) && "opacity-50"
                )}
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
              >
                {selectedCount && selectedCount > 0 && (
                  <span className="text-xs text-semantic-secondary-foreground-heavy pl-1">
                    {selectedCount}
                  </span>
                )}
                {icon && (
                  <span className="relative flex items-center">
                    {icon}
                    <div
                      role="button"
                      tabIndex={isInteractive ? 0 : -1}
                      onClick={handleClearClick}
                      onMouseDown={(e) => e.preventDefault()}
                      className={cn(
                        iconOverlayStyles(),
                        !isInteractive ? "!cursor-default" : ""
                      )}
                      aria-disabled={!isInteractive}
                      aria-label="Clear selection"
                    />
                  </span>
                )}
                {clearIcon && isInteractive && (
                  <button
                    tabIndex={isInteractive ? 0 : -1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClearClick}
                    className={cn(
                      "transition duration-100",
                      !isInteractive
                        ? "opacity-50 !cursor-default"
                        : "cursor-pointer rounded-lg hover:bg-semantic-error"
                    )}
                    aria-disabled={!isInteractive}
                  >
                    {clearIcon}
                  </button>
                )}
              </div>
            ) : (
              clearIcon &&
              isInteractive && (
                <div
                  className={cn(
                    "relative flex items-center z-10",
                    (disabled || readonly || plainReadOnly) && "opacity-50"
                  )}
                  onPointerDown={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {icon && (
                    <span className="relative flex items-center">
                      {icon}
                      <div
                        role="button"
                        tabIndex={isInteractive ? 0 : -1}
                        onClick={handleClearClick}
                        onMouseDown={(e) => e.preventDefault()}
                        className={cn(
                          iconOverlayStyles(),
                          !isInteractive ? "!cursor-default" : ""
                        )}
                        aria-disabled={!isInteractive}
                        aria-label="Clear selection"
                      />
                    </span>
                  )}
                  <button
                    tabIndex={isInteractive ? 0 : -1}
                    onClick={handleClearClick}
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(
                      "size-5",
                      isInteractive
                        ? "cursor-pointer"
                        : "!cursor-default opacity-50"
                    )}
                    aria-disabled={!isInteractive}
                  >
                    {clearIcon}
                  </button>
                </div>
              )
            )}
          </>
        )}
        <div
          className={cn(
            "w-full text-left overflow-hidden text-ellipsis whitespace-nowrap",
            clearIcon &&
              (mode === "multiple" && hasValue
                ? "pl-10"
                : mode === "single" && hasValue
                  ? ""
                  : "pl-0"),
            (disabled || readonly || plainReadOnly) && "opacity-50"
          )}
        >
          <div
            className={cn(
              "overflow-hidden text-ellipsis whitespace-nowrap typography-semantic-label-1-normal---regular"
            )}
          >
            {renderSelectedValues()}
          </div>
        </div>

        <div
          className={cn(
            "size-5",
            (disabled || readonly || plainReadOnly) && "opacity-50"
          )}
        >
          <ArrowDown
            className={cn(
              "transition-transform duration-200",
              (disabled || readonly || plainReadOnly) && "opacity-50",
              {
                "-rotate-180": open && isInteractive,
              }
            )}
          />
        </div>
      </div>
    );
  }
);

export default SelectDropdownField;
